// Shared stroke model for the collaborative whiteboard + screen annotation.
// Coordinates are NORMALIZED to [0,1] relative to the drawing surface so a
// stroke renders identically regardless of each peer's panel size, and brush
// widths scale with the surface. Strokes live in a Yjs array (durable for the
// session) and stream live via awareness while in progress.

export type StrokeTool = "pen" | "eraser"

// A point in [0,1] x [0,1], relative to the surface's top-left.
export type NormPoint = [number, number]

export interface Stroke {
  id: string
  color: string
  // Brush width at a 1000px reference surface; scaled to the real size on render.
  size: number
  tool: StrokeTool
  points: NormPoint[]
}

// Reference surface width the brush `size` is calibrated against.
const REFERENCE_WIDTH = 1000

function clamp01(value: number): number {
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

// Stable, collision-free stroke id without randomness: the author's client id
// plus a per-author counter.
export function makeStrokeId(clientId: number, counter: number): string {
  return `${clientId}-${counter}`
}

// The counter lives at module scope, NOT in the canvas component: the board
// unmounts every time it's closed while the Y.Doc (and clientID) live on for
// the lesson, and a per-mount counter would reuse ids after a reopen — a
// delete-by-id could then find an older stroke first and erase a line nobody
// touched. A page reload gets a fresh clientID, so per-load uniqueness is
// enough.
let strokeCounter = 0
export function nextStrokeCounter(): number {
  return strokeCounter++
}

// Convert a pointer's client coordinates to a normalized surface point.
export function toNormalized(clientX: number, clientY: number, rect: DOMRect): NormPoint {
  const x = rect.width === 0 ? 0 : (clientX - rect.left) / rect.width
  const y = rect.height === 0 ? 0 : (clientY - rect.top) / rect.height
  return [clamp01(x), clamp01(y)]
}

// Evenly thin a point list down to at most `max` points, always keeping the
// last one so a live preview still reaches the cursor. Used only for the
// in-progress (awareness) preview — committed strokes keep every point.
export function downsamplePoints(points: NormPoint[], max: number): NormPoint[] {
  if (max < 2 || points.length <= max) return points
  const step = points.length / max
  const out: NormPoint[] = []
  for (let i = 0; i < max; i++) out.push(points[Math.floor(i * step)])
  const last = points[points.length - 1]
  if (out[out.length - 1] !== last) out.push(last)
  return out
}

// The effective line width (CSS px) for a stroke on a surface of the given size.
export function strokeLineWidth(stroke: Pick<Stroke, "size" | "tool">, width: number): number {
  const base = Math.max(1, (stroke.size * width) / REFERENCE_WIDTH)
  return stroke.tool === "eraser" ? base * 4 : base
}

// ---------------------------------------------------------------------------
// Eraser geometry (#942 / #1022). All distances are measured in CSS px on the
// real w x h surface — normalized units would warp with the aspect ratio.

// The whole-line eraser's hit radius. Its size never changes (the size slider
// is greyed out in that mode), so this is a plain, forgiving click target.
export const LINE_ERASER_HIT_RADIUS_PX = 10

type PxPoint = [number, number]

function pointSegmentDistance(p: PxPoint, a: PxPoint, b: PxPoint): number {
  const abx = b[0] - a[0]
  const aby = b[1] - a[1]
  const lengthSq = abx * abx + aby * aby
  let t = 0
  if (lengthSq > 0) {
    t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / lengthSq
    t = Math.max(0, Math.min(1, t))
  }
  const dx = p[0] - (a[0] + t * abx)
  const dy = p[1] - (a[1] + t * aby)
  return Math.hypot(dx, dy)
}

function orientation(a: PxPoint, b: PxPoint, c: PxPoint): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
}

function segmentsIntersect(a: PxPoint, b: PxPoint, c: PxPoint, d: PxPoint): boolean {
  const o1 = orientation(a, b, c)
  const o2 = orientation(a, b, d)
  const o3 = orientation(c, d, a)
  const o4 = orientation(c, d, b)
  // Proper crossing only; touching endpoints fall to the distance checks,
  // which handle them (distance 0).
  return o1 * o2 < 0 && o3 * o4 < 0
}

function segmentSegmentDistance(a: PxPoint, b: PxPoint, c: PxPoint, d: PxPoint): number {
  if (segmentsIntersect(a, b, c, d)) return 0
  return Math.min(
    pointSegmentDistance(a, c, d),
    pointSegmentDistance(b, c, d),
    pointSegmentDistance(c, a, b),
    pointSegmentDistance(d, a, b),
  )
}

const toPx = (p: NormPoint, width: number, height: number): PxPoint => [p[0] * width, p[1] * height]

// Whole-line mode: the ids of the pen strokes the eraser's path segment
// [a, b] touches, in board order. "Touches" counts the stroke's visible width,
// so grazing a fat line's edge still hits it. Legacy rub-out eraser strokes
// are invisible — never hit. A stationary tap (a == b) works too.
export function strokesTouchedBySegment(
  strokes: readonly Stroke[],
  a: NormPoint,
  b: NormPoint,
  radiusPx: number,
  width: number,
  height: number,
): string[] {
  const pa = toPx(a, width, height)
  const pb = toPx(b, width, height)
  const hits: string[] = []
  for (const stroke of strokes) {
    if (stroke.tool === "eraser" || stroke.points.length === 0) continue
    const reach = radiusPx + strokeLineWidth(stroke, width) / 2
    const pts = stroke.points
    if (pts.length === 1) {
      if (pointSegmentDistance(toPx(pts[0], width, height), pa, pb) <= reach) hits.push(stroke.id)
      continue
    }
    let prev = toPx(pts[0], width, height)
    for (let i = 1; i < pts.length; i++) {
      const next = toPx(pts[i], width, height)
      if (segmentSegmentDistance(prev, next, pa, pb) <= reach) {
        hits.push(stroke.id)
        break
      }
      prev = next
    }
  }
  return hits
}

// Area mode (#1022): really remove the ink under the eraser's path segment
// instead of committing an invisible cover-up stroke. Returns null when the
// stroke is untouched (keep the object as-is), else the surviving point runs —
// [] deletes the stroke, 1+ runs replace it with fragments.
//
// The stroke's polyline is resampled first so a fast, sparse straight line
// still splits where it was rubbed, not only at its recorded points. Runs that
// shrink below 2 points are residue crumbs and are dropped.
export function eraseAreaFromStroke(
  stroke: Stroke,
  a: NormPoint,
  b: NormPoint,
  radiusPx: number,
  width: number,
  height: number,
): NormPoint[][] | null {
  if (stroke.tool === "eraser" || stroke.points.length === 0) return null
  const pa = toPx(a, width, height)
  const pb = toPx(b, width, height)

  // A dot goes whole or not at all, counting its own visible radius.
  if (stroke.points.length === 1) {
    const reach = radiusPx + strokeLineWidth(stroke, width) / 2
    return pointSegmentDistance(toPx(stroke.points[0], width, height), pa, pb) <= reach ? [] : null
  }

  // Quick reject before resampling: nothing near the eraser, nothing to do.
  let near = false
  let prev = toPx(stroke.points[0], width, height)
  for (let i = 1; i < stroke.points.length && !near; i++) {
    const next = toPx(stroke.points[i], width, height)
    if (segmentSegmentDistance(prev, next, pa, pb) <= radiusPx) near = true
    prev = next
  }
  if (!near) return null

  // Resample finely enough that the eraser can't slip between two samples.
  const step = Math.max(2, radiusPx / 2)
  const sampled: PxPoint[] = [toPx(stroke.points[0], width, height)]
  for (let i = 1; i < stroke.points.length; i++) {
    const from = sampled[sampled.length - 1]
    const to = toPx(stroke.points[i], width, height)
    const dist = Math.hypot(to[0] - from[0], to[1] - from[1])
    const pieces = Math.max(1, Math.ceil(dist / step))
    for (let k = 1; k <= pieces; k++) {
      sampled.push([from[0] + ((to[0] - from[0]) * k) / pieces, from[1] + ((to[1] - from[1]) * k) / pieces])
    }
  }

  const runs: NormPoint[][] = []
  let current: NormPoint[] = []
  let removedAny = false
  for (const p of sampled) {
    if (pointSegmentDistance(p, pa, pb) <= radiusPx) {
      removedAny = true
      if (current.length >= 2) runs.push(current)
      current = []
    } else {
      current.push([clamp01(p[0] / width), clamp01(p[1] / height)])
    }
  }
  if (current.length >= 2) runs.push(current)

  return removedAny ? runs : null
}

// The local brush cursor (#951): your own pointer drawn as a circle the exact
// size of what the brush covers, like Photoshop. Only you see it; the other
// participant keeps seeing your awareness dot.

// Smallest radius (CSS px) the cursor's rim is drawn at. The thinnest pen is a
// pixel wide, so at tiny sizes the rim stays a small ring round the true-size
// disc and the cursor never disappears.
export const BRUSH_CURSOR_MIN_RADIUS = 3

// A two-tone rim, light then dark, so the circle shows on the white board and
// on a transparent layer over a dark screen share alike. Round the pen's disc
// it also tells the cursor apart from ink already on the board.
const BRUSH_CURSOR_RIM_LIGHT = "rgba(255,255,255,0.9)"
const BRUSH_CURSOR_RIM_DARK = "rgba(0,0,0,0.6)"

export interface BrushCursorShape {
  // What the brush covers: the pen's line width, the eraser's rubbed-out area.
  diameter: number
  // The edge the rim is drawn on, from the centre: the brush's own, or the
  // minimum marker's when the brush is too small to see.
  rimRadius: number
}

// The cursor's geometry for a brush on a surface `width` CSS px wide. Sized by
// strokeLineWidth, the width the line itself is drawn with, so the circle
// can't drift from the ink (the eraser's 4x, a resized board, all of it).
export function brushCursorShape(brush: Pick<Stroke, "size" | "tool">, width: number): BrushCursorShape {
  const diameter = strokeLineWidth(brush, width)
  return { diameter, rimRadius: Math.max(diameter / 2, BRUSH_CURSOR_MIN_RADIUS) }
}

// Paint the brush cursor centred on (x, y) CSS px: the pen as a disc in its
// colour, the eraser as an outline of the area it rubs out. The rim sits on the
// brush's edge, so it shows exactly what the brush covers (bar the minimum
// ring, which stays wider than the thinnest brushes).
export function drawBrushCursor(
  ctx: CanvasRenderingContext2D,
  brush: Pick<Stroke, "size" | "tool" | "color">,
  x: number,
  y: number,
  width: number,
): void {
  const { diameter, rimRadius } = brushCursorShape(brush, width)

  ctx.save()
  ctx.globalCompositeOperation = "source-over"
  if (brush.tool === "pen") {
    ctx.beginPath()
    ctx.arc(x, y, diameter / 2, 0, Math.PI * 2)
    ctx.fillStyle = brush.color
    ctx.fill()
  }
  // Two 1px rings, the dark one just outside the light one. Round the pen they
  // hug the outside of its disc, which stays true to size. Round the eraser
  // they meet on its edge: the dark ring starts where the rubbing out ends (the
  // white board) and the light ring ends there (a dark screen share).
  const lightRadius = brush.tool === "pen" ? rimRadius + 0.5 : rimRadius - 0.5
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.arc(x, y, lightRadius, 0, Math.PI * 2)
  ctx.strokeStyle = BRUSH_CURSOR_RIM_LIGHT
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(x, y, lightRadius + 1, 0, Math.PI * 2)
  ctx.strokeStyle = BRUSH_CURSOR_RIM_DARK
  ctx.stroke()
  ctx.restore()
}

// Render one stroke onto a 2D context whose drawing area is width x height CSS
// px. Pen draws in the stroke color; eraser removes pixels (destination-out) so
// it works on both the white board and the transparent annotation overlay.
export function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  width: number,
  height: number,
): void {
  const pts = stroke.points
  if (pts.length === 0) return

  ctx.save()
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.lineWidth = strokeLineWidth(stroke, width)
  if (stroke.tool === "eraser") {
    ctx.globalCompositeOperation = "destination-out"
    ctx.strokeStyle = "rgba(0,0,0,1)"
  } else {
    ctx.globalCompositeOperation = "source-over"
    ctx.strokeStyle = stroke.color
  }

  ctx.beginPath()
  ctx.moveTo(pts[0][0] * width, pts[0][1] * height)
  if (pts.length === 1) {
    // A single tap: nudge so round line-caps render a dot.
    ctx.lineTo(pts[0][0] * width + 0.01, pts[0][1] * height + 0.01)
  } else {
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i][0] * width, pts[i][1] * height)
    }
  }
  ctx.stroke()
  ctx.restore()
}
